import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Crown, Sparkles, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { API } from "../lib/helpers";

/**
 * Trigger-based upsell popups (Pro → Elite and Pro Monthly → Yearly).
 *
 * Rules (from product spec):
 *   - Delay popup 3–5s after the trigger/page is ready (never on page load)
 *   - Never show on sensitive routes (billing, pricing, auth, admin, landing)
 *   - Respect dismiss for 7 days UNLESS a NEW trigger fires (different key)
 *   - Never stack — backend returns only one popup at a time
 *   - Track: upsell_popup_viewed, upsell_popup_clicked, upsell_popup_dismissed
 */

const BLOCKED_ROUTES = new Set([
  "/",
  "/auth",
  "/billing",
  "/pricing",
  "/admin",
  "/unsubscribe",
  "/reset-password",
  "/contact",
  "/business",
]);

const SHOW_DELAY_MS = 4000; // 4s after eligibility before showing
const POLL_DELAY_MS = 15000; // don't re-check too frequently
const DEFER_CHECK_MS = 4000; // when another modal is open, re-check this often
const MAX_DEFERS = 75; // stop trying after ~5 min; never show over another dialog

function isRouteBlocked(pathname) {
  if (!pathname) return true;
  if (BLOCKED_ROUTES.has(pathname)) return true;
  // Block any nested admin route & Stripe success/cancel flows.
  if (pathname.startsWith("/admin")) return true;
  if (pathname.startsWith("/billing")) return true;
  return false;
}

function hasOtherOpenDialog() {
  // Detect any non-upsell Radix dialog already open (onboarding, analysis
  // modal, export flow, etc.) so we never stack on top of user work.
  if (typeof document === "undefined") return false;
  const openDialogs = document.querySelectorAll(
    '[role="dialog"][data-state="open"]:not([data-testid^="upsell-popup-"])'
  );
  return openDialogs.length > 0;
}

function dismissKey(type) {
  return `upsell_popup_dismiss_${type}`;
}

function readDismiss(type) {
  try {
    const raw = localStorage.getItem(dismissKey(type));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function writeDismiss(type, trigger, days) {
  try {
    const until = Date.now() + days * 24 * 60 * 60 * 1000;
    localStorage.setItem(
      dismissKey(type),
      JSON.stringify({ until, trigger })
    );
  } catch (_) {
    /* ignore */
  }
}

function isDismissedForCurrentTrigger(type, trigger) {
  const d = readDismiss(type);
  if (!d) return false;
  // If a NEW trigger has fired (different key), ignore the old dismissal.
  if (d.trigger && d.trigger !== trigger) return false;
  return Date.now() < d.until;
}

export default function UpsellPopupManager({ isAuthenticated }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [popup, setPopup] = useState(null);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const tryShowRef = useRef(null);
  const lastCheckedAt = useRef(0);
  const viewedKeyRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Core: check eligibility when route changes (and on mount)
  useEffect(() => {
    clearTimer();
    if (!isAuthenticated) return;
    if (isRouteBlocked(location.pathname)) return;

    // Throttle re-checks to avoid hammering the API on every route change
    const now = Date.now();
    if (open) return; // already showing
    if (now - lastCheckedAt.current < POLL_DELAY_MS && popup) {
      // Re-use existing eligible popup, just re-arm the timer
    } else {
      lastCheckedAt.current = now;
    }

    let cancelled = false;

    axios
      .get(`${API}/upsell-popup/next`)
      .then((r) => {
        if (cancelled) return;
        const next = r.data?.popup || null;
        if (!next) {
          setPopup(null);
          return;
        }
        // Respect per-type dismissal unless trigger changed.
        if (isDismissedForCurrentTrigger(next.type, next.trigger)) {
          setPopup(null);
          return;
        }
        setPopup(next);

        // 4-second delay before showing — never on page load.
        // Defer if another modal (e.g., onboarding) is already open so we
        // never stack popups on top of each other.
        let deferCount = 0;
        const tryShow = () => {
          if (isRouteBlocked(window.location.pathname)) return;
          if (hasOtherOpenDialog()) {
            // Never appear over an active modal; give up quietly after the cap.
            if (deferCount >= MAX_DEFERS) return;
            deferCount += 1;
            timerRef.current = setTimeout(tryShow, DEFER_CHECK_MS);
            return;
          }
          setOpen(true);
        };
        tryShowRef.current = tryShow;
        timerRef.current = setTimeout(tryShow, SHOW_DELAY_MS);
      })
      .catch(() => {
        // Silent fail — popups are non-critical.
      });

    return () => {
      cancelled = true;
      clearTimer();
    };
    // Re-run on route changes so a user hitting /dashboard re-qualifies the popup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, location.pathname]);

  // If the user opens another dialog (e.g. analysis modal) while the popup is
  // showing, yield immediately without recording a dismissal and retry later.
  useEffect(() => {
    if (!open || typeof MutationObserver === "undefined") return;
    const yieldIfNeeded = () => {
      if (!hasOtherOpenDialog()) return;
      setOpen(false);
      clearTimer();
      if (tryShowRef.current) timerRef.current = setTimeout(tryShowRef.current, DEFER_CHECK_MS);
    };
    const mo = new MutationObserver(yieldIfNeeded);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state"] });
    yieldIfNeeded();
    return () => mo.disconnect();
  }, [open, clearTimer]);

  // Fire viewed event exactly once per (type, trigger) session pair.
  useEffect(() => {
    if (!open || !popup) return;
    const k = `upsell_popup_viewed_${popup.type}_${popup.trigger}`;
    if (viewedKeyRef.current === k) return;
    viewedKeyRef.current = k;
    try {
      if (sessionStorage.getItem(k)) return;
      sessionStorage.setItem(k, "1");
    } catch (_) {
      /* ignore */
    }
    axios
      .post(`${API}/analytics/track`, {
        event_type: "upsell_popup_viewed",
        metadata: {
          type: popup.type,
          trigger: popup.trigger,
          signals: popup.signals,
        },
      })
      .catch(() => {});
  }, [open, popup]);

  const handleUpgrade = useCallback(() => {
    if (!popup) return;
    axios
      .post(`${API}/analytics/track`, {
        event_type: "upsell_popup_clicked",
        metadata: { type: popup.type, trigger: popup.trigger },
      })
      .catch(() => {});
    setOpen(false);
    navigate(`/billing?tier=${popup.primary_tier}&src=popup_${popup.type}`);
  }, [popup, navigate]);

  const handleDismiss = useCallback(() => {
    if (!popup) return;
    writeDismiss(popup.type, popup.trigger, popup.dismiss_days || 7);
    axios
      .post(`${API}/analytics/track`, {
        event_type: "upsell_popup_dismissed",
        metadata: { type: popup.type, trigger: popup.trigger },
      })
      .catch(() => {});
    setOpen(false);
  }, [popup]);

  if (!popup) return null;

  const isElite = popup.type === "pro_to_elite";
  const accent = isElite
    ? "from-amber-500/20 via-amber-500/5"
    : "from-emerald-500/20 via-emerald-500/5";
  const Icon = isElite ? Crown : Sparkles;
  const accentText = isElite ? "text-amber-500" : "text-emerald-500";
  const btnClass = isElite
    ? "bg-amber-500 hover:bg-amber-600 text-black font-semibold"
    : "bg-emerald-600 hover:bg-emerald-700 text-white font-semibold";

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : handleDismiss())}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden border-border/60"
        data-testid={`upsell-popup-${popup.type}`}
      >
        <div
          className={`relative bg-gradient-to-br ${accent} to-transparent p-6 sm:p-7`}
        >
          <button
            onClick={handleDismiss}
            aria-label="Dismiss"
            data-testid="upsell-popup-close"
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors p-1"
          >
            <X className="w-4 h-4" />
          </button>

          <div className={`inline-flex p-3 rounded-full bg-background/50 mb-4 ${accentText}`}>
            <Icon className="w-6 h-6" />
          </div>

          <DialogTitle className="text-xl sm:text-2xl font-semibold leading-tight mb-2">
            {popup.headline}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
            {popup.subtext}
          </DialogDescription>

          <div className="flex flex-col sm:flex-row gap-2 mt-6">
            <Button
              onClick={handleUpgrade}
              data-testid="upsell-popup-upgrade"
              className={`${btnClass} flex-1 sm:flex-none`}
            >
              {popup.primary_cta}
            </Button>
            <Button
              variant="ghost"
              onClick={handleDismiss}
              data-testid="upsell-popup-dismiss"
              className="text-muted-foreground hover:text-foreground"
            >
              Maybe later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
